import { useState } from "react";
import { useAdvisorStore } from "@/features/auth/stores/advisorStore";
import { Card, CardContent, CardHeader, CardTitle, Button, Input } from "@/shared/components/ui";
import { PublicProfileSettings } from "@/shared/components/PublicProfileSettings";
import { formatDateTime } from "@/shared/lib/utils";
import { User, Save, AlertCircle, CheckCircle, CreditCard, Building, Phone, Globe } from "lucide-react";

type TabType = "basic" | "public";

export function ProfilePage() {
  const { advisor, updateProfile, isLoading, error, clearError } = useAdvisorStore();
  const [activeTab, setActiveTab] = useState<TabType>("basic");
  const [isEditing, setIsEditing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    name: advisor?.name || "",
    company: advisor?.company || "",
    phone: advisor?.phone || "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSuccess(false);

    try {
      await updateProfile(form);
      setSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      // Error is handled by the store
    }
  };

  const handleCancel = () => {
    setForm({
      name: advisor?.name || "",
      company: advisor?.company || "",
      phone: advisor?.phone || "",
    });
    setIsEditing(false);
  };

  const tabs = [
    { id: "basic" as const, label: "Dane podstawowe", icon: User },
    { id: "public" as const, label: "Profil publiczny", icon: Globe },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profil</h1>
        <p className="text-gray-600 mt-1">Zarządzaj swoimi danymi i profilem publicznym</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "basic" ? (
        <div className="space-y-6 max-w-2xl">
          {/* Profile Info */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Dane osobowe
              </CardTitle>
              {!isEditing && (
                <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                  Edytuj
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {success && (
                <div className="mb-4 p-3 bg-success-50 border border-success-200 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-success-600" />
                  <p className="text-sm text-success-700">Profil został zaktualizowany</p>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                  <p className="text-sm text-error-700">{error}</p>
                </div>
              )}

              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Imię i nazwisko"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    required
                  />
                  <Input
                    label="Firma"
                    value={form.company}
                    onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  />
                  <Input
                    label="Telefon"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                  <div className="flex gap-3 pt-2">
                    <Button type="submit" isLoading={isLoading} leftIcon={<Save className="w-4 h-4" />}>
                      Zapisz zmiany
                    </Button>
                    <Button type="button" variant="secondary" onClick={handleCancel}>
                      Anuluj
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600">Email</p>
                    <p className="font-medium">{advisor?.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Imię i nazwisko</p>
                    <p className="font-medium">{advisor?.name || "-"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Firma</p>
                      <p className="font-medium">{advisor?.company || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-600">Telefon</p>
                      <p className="font-medium">{advisor?.phone || "-"}</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Konto
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-primary-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Dostępne kredyty</p>
                  <p className="text-2xl font-bold text-primary-700">{advisor?.credits || 0}</p>
                </div>
                <CreditCard className="w-8 h-8 text-primary-400" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Status konta</p>
                  <p className="font-medium capitalize">
                    {advisor?.status === "active"
                      ? "Aktywny"
                      : advisor?.status === "pending"
                        ? "Oczekujący"
                        : advisor?.status}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Data rejestracji</p>
                  <p className="font-medium">
                    {advisor?.createdAt ? formatDateTime(advisor.createdAt) : "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <PublicProfileSettings />
      )}
    </div>
  );
}
